import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageCircle, Send, X, Loader2, ChevronLeft, 
  User, HeartHandshake, Clock, Trash2, Wifi, WifiOff
} from 'lucide-react';
import { messagingAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import useChatWebSocket from '../hooks/useChatWebSocket';
import { showChatNotification, getNotificationPermission } from '../services/pushNotifications';
import { useAuth } from '../context/AuthContext';

// Chat Button Component - Opens chat modal
export const ChatSupportButton = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchUnread = async () => {
    try {
      const response = await messagingAPI.getUnreadCount();
      setUnreadCount(response.data.unread_count);
    } catch {
      // Silently fail for unread count
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-2 relative ${className}`}
        data-testid="chat-support-btn"
      >
        <MessageCircle className="w-5 h-5" />
        <span>Chat with Support</span>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <ChatModal 
          onClose={() => { setIsOpen(false); fetchUnread(); }} 
          type="support" 
        />
      )}
    </>
  );
};

// Chat Modal Component
export const ChatModal = ({ onClose, type = 'support', bookingId = null, initialConversation = null }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(initialConversation);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [view, setView] = useState(initialConversation ? 'chat' : 'list');
  const [typingUsers, setTypingUsers] = useState({});
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  // Current user ID for message ownership check
  const currentUserId = user?.id;

  // WebSocket handlers
  const handleNewMessage = useCallback((data) => {
    if (data.conversation_id === activeConversation?.id) {
      setMessages(prev => {
        // Avoid duplicates
        if (prev.some(m => m.id === data.id)) return prev;
        return [...prev, data];
      });
    }
    // Update conversations list
    setConversations(prev => prev.map(conv => 
      conv.id === data.conversation_id 
        ? { ...conv, last_message: data.content, last_message_time: data.created_at }
        : conv
    ));
    
    // Show browser notification if page is not focused and permission granted
    if (document.hidden && getNotificationPermission() === 'granted') {
      showChatNotification(
        data.sender_name || 'New Message',
        data.content,
        () => {
          // Focus on conversation when notification clicked
          setActiveConversation(prev => prev?.id === data.conversation_id ? prev : null);
        }
      );
    }
  }, [activeConversation?.id]);

  const handleTyping = useCallback((data) => {
    if (data.conversation_id === activeConversation?.id) {
      setTypingUsers(prev => ({ ...prev, [data.user_id]: data.user_name }));
    }
  }, [activeConversation?.id]);

  const handleStopTyping = useCallback((data) => {
    if (data.conversation_id === activeConversation?.id) {
      setTypingUsers(prev => {
        const updated = { ...prev };
        delete updated[data.user_id];
        return updated;
      });
    }
  }, [activeConversation?.id]);

  const handleMessagesRead = useCallback((data) => {
    if (data.conversation_id === activeConversation?.id) {
      setMessages(prev => prev.map(m => ({ ...m, read: true })));
    }
  }, [activeConversation?.id]);

  // Initialize WebSocket
  const { 
    isConnected, 
    subscribe, 
    unsubscribe, 
    sendMessage: wsSendMessage,
    sendTyping,
    sendStopTyping,
    markAsRead
  } = useChatWebSocket(handleNewMessage, handleTyping, handleStopTyping, handleMessagesRead);

  useEffect(() => {
    if (type === 'support' && !initialConversation) {
      startSupportChat();
    } else if (type === 'booking' && bookingId) {
      startBookingChat();
    } else {
      fetchConversations();
    }
  }, [type, bookingId]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages();
      // Subscribe to conversation via WebSocket
      if (isConnected) {
        subscribe(activeConversation.id);
        markAsRead(activeConversation.id);
      }
      // Fallback polling only if WebSocket not connected
      const interval = isConnected ? null : setInterval(fetchMessages, 5000);
      return () => {
        if (interval) clearInterval(interval);
        if (isConnected) unsubscribe(activeConversation.id);
      };
    }
  }, [activeConversation?.id, isConnected]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await messagingAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSupportChat = async () => {
    try {
      const response = await messagingAPI.startSupportChat();
      setActiveConversation(response.data);
      setView('chat');
    } catch (error) {
      toast.error('Unable to start support chat');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const startBookingChat = async () => {
    try {
      const response = await messagingAPI.startBookingChat(bookingId);
      setActiveConversation(response.data);
      setView('chat');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to start chat');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!activeConversation) return;
    try {
      const response = await messagingAPI.getConversation(activeConversation.id);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    const messageContent = newMessage.trim();
    setSending(true);
    setNewMessage('');
    
    // Stop typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      sendStopTyping(activeConversation.id);
    }

    try {
      // Try WebSocket first, fallback to HTTP
      if (isConnected) {
        const sent = wsSendMessage(activeConversation.id, messageContent);
        if (!sent) {
          // Fallback to HTTP if WebSocket send failed
          await messagingAPI.sendMessage(activeConversation.id, messageContent);
          fetchMessages();
        }
      } else {
        await messagingAPI.sendMessage(activeConversation.id, messageContent);
        fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(messageContent); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (isConnected && activeConversation && e.target.value) {
      sendTyping(activeConversation.id);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        sendStopTyping(activeConversation.id);
      }, 2000);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }
    
    try {
      await messagingAPI.deleteConversation(conversationId);
      toast.success('Conversation deleted');
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
        setView('list');
      }
      fetchConversations();
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[var(--background-paper)] rounded-2xl shadow-2xl w-full max-w-lg h-[600px] max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {view === 'chat' && conversations.length > 1 && (
                <button onClick={() => setView('list')} className="p-1 hover:bg-[var(--background-paper)]/20 rounded">
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <HeartHandshake className="w-8 h-8" />
              <div>
                <h2 className="font-bold">
                  {view === 'chat' && activeConversation
                    ? activeConversation.conversation_type === 'support'
                      ? 'NC Help Hub Support'
                      : `Booking Chat`
                    : 'Messages'
                  }
                </h2>
                <div className="flex items-center gap-2">
                  {view === 'chat' && activeConversation?.booking_service && (
                    <p className="text-xs opacity-80">{activeConversation.booking_service}</p>
                  )}
                  {/* WebSocket Connection Status */}
                  <span className={`flex items-center gap-1 text-xs ${isConnected ? 'text-green-200' : 'text-yellow-200'}`}>
                    {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                    {isConnected ? 'Live' : 'Offline'}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--background-paper)]/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : view === 'list' ? (
          /* Conversation List */
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-[var(--text-muted)]">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No conversations yet</p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--border-rose)]">
                {conversations.map(conv => (
                  <div
                    key={conv.id}
                    className="p-4 hover:bg-[var(--background-subtle)] transition-colors flex items-center gap-3"
                  >
                    <button
                      onClick={() => { setActiveConversation(conv); setView('chat'); }}
                      className="flex-1 flex items-center gap-3 text-left"
                    >
                      <div className="w-10 h-10 bg-[var(--accent)] rounded-full flex items-center justify-center">
                        {conv.conversation_type === 'support' ? (
                          <HeartHandshake className="w-5 h-5 text-[var(--primary)]" />
                        ) : (
                          <User className="w-5 h-5 text-[var(--primary)]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium truncate">
                            {conv.conversation_type === 'support' ? 'Support' : conv.booking_service || 'Booking Chat'}
                          </span>
                          {conv.last_message_time && (
                            <span className="text-xs text-[var(--text-muted)]">
                              {formatTime(conv.last_message_time)}
                            </span>
                          )}
                        </div>
                        {conv.last_message && (
                          <p className="text-sm text-[var(--text-muted)] truncate">{conv.last_message}</p>
                        )}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-[var(--primary)] text-white text-xs rounded-full flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv.id); }}
                      className="p-2 text-[var(--error)] hover:bg-[var(--error)]/10 rounded-full transition-colors"
                      title="Delete conversation"
                      data-testid={`delete-conv-${conv.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Chat View */
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--background-subtle)]">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-[var(--text-muted)]">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Start the conversation!</p>
                  <p className="text-sm">Send a message below</p>
                </div>
              ) : (
                messages.map(msg => {
                  const isOwn = msg.sender_id === currentUserId;
                  return (
                    <div 
                      key={msg.id} 
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] ${isOwn ? 'order-2' : ''}`}>
                        {!isOwn && (
                          <p className="text-xs text-[var(--text-muted)] mb-1 px-1">
                            {msg.sender_name}
                          </p>
                        )}
                        <div className={`p-3 rounded-2xl ${
                          isOwn 
                            ? 'bg-[var(--primary)] text-white rounded-br-sm' 
                            : 'bg-[var(--background-paper)] text-[var(--text-primary)] rounded-bl-sm shadow-sm'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        <p className={`text-xs text-[var(--text-muted)] mt-1 px-1 ${isOwn ? 'text-right' : ''}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              
              {/* Typing Indicator */}
              {Object.keys(typingUsers).length > 0 && (
                <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm pl-2 pb-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[var(--primary)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-[var(--primary)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-[var(--primary)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <span>{Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing...</span>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSend} className="p-4 border-t border-[var(--border-primary)] bg-[var(--background-paper)]">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={handleInputChange}
                  placeholder="Type your message..."
                  className="flex-1"
                  disabled={sending}
                  data-testid="message-input"
                />
                <Button 
                  type="submit" 
                  disabled={!newMessage.trim() || sending}
                  className="btn-primary px-4"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

// Inline Chat Component for Dashboards
export const InlineChat = ({ conversationId, className = '' }) => {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await messagingAPI.getConversation(conversationId);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await messagingAPI.sendMessage(conversationId, newMessage.trim());
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <Loader2 className="w-6 h-6 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--background-subtle)] rounded-t-xl max-h-[400px]">
        {messages.length === 0 ? (
          <div className="text-center py-4 text-[var(--text-muted)]">
            <p className="text-sm">No messages yet</p>
          </div>
        ) : (
          messages.map(msg => {
            const isOwn = msg.sender_id === currentUserId;
            return (
              <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%]`}>
                  {!isOwn && (
                    <p className="text-xs text-[var(--text-muted)] mb-1">{msg.sender_name}</p>
                  )}
                  <div className={`p-3 rounded-xl text-sm ${
                    isOwn 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--background-paper)] shadow-sm'
                  }`}>
                    {msg.content}
                  </div>
                  <p className={`text-xs text-[var(--text-muted)] mt-1 ${isOwn ? 'text-right' : ''}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="p-3 border-t border-[var(--border-primary)] bg-[var(--background-paper)] rounded-b-xl">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 text-sm"
            disabled={sending}
          />
          <Button type="submit" disabled={!newMessage.trim() || sending} size="sm" className="btn-primary">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default { ChatSupportButton, ChatModal, InlineChat };
